import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from "react";
import clsx from "clsx";
import type { PlaceResult, POI } from "@poi-planner/shared";
import { useLibraryStore } from "../store/libraryStore";
import { useTripStore } from "../store/tripStore";
import { usePlacesSearch } from "../hooks/usePlacesSearch";
import { Button } from "./Button";

/** A library POI, or a Google Places result that becomes one on save. */
export type PoiComboboxValue =
  | { kind: "poi"; poiId: string }
  | { kind: "place"; place: PlaceResult };

type Option = { kind: "poi"; poi: POI } | { kind: "place"; place: PlaceResult };

const MAX_LIBRARY_OPTIONS = 6;
const MAX_PLACE_OPTIONS = 5;
const MIN_PLACES_QUERY = 2;

function optionLabel(option: Option): string {
  return option.kind === "poi" ? option.poi.name : option.place.name;
}

/**
 * Type-ahead POI picker. An empty query lists places already in this trip;
 * typing searches the library first and Google Places after it. Picking a
 * Places result doesn't touch the library: the caller creates the POI when
 * it saves, so cancelling leaves nothing behind.
 */
export function PoiCombobox({
  label,
  value,
  onChange,
  icon,
  placeholder = "Search places",
  disabled = false,
  inputRef: externalInputRef,
}: {
  label: string;
  value: PoiComboboxValue | null;
  onChange: (value: PoiComboboxValue | null) => void;
  /** Material Symbols glyph shown in the field. */
  icon: string;
  placeholder?: string;
  disabled?: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
}) {
  const id = useId();
  const inputId = `${id}-input`;
  const listId = `${id}-list`;
  const pois = useLibraryStore((state) => state.pois);
  const pinnedPoiIds = useTripStore((state) => state.pinnedPoiIds);
  const scheduledPois = useTripStore((state) => state.schedule.scheduledPOIs);
  const places = usePlacesSearch();
  const ownInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = externalInputRef ?? ownInputRef;

  /** Text being typed; null shows the selected value's name instead. */
  const [draft, setDraft] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const selectedName = !value
    ? ""
    : value.kind === "place"
      ? value.place.name
      : (pois.find((poi) => poi.id === value.poiId)?.name ?? "Unknown place");
  const query = (draft ?? "").trim().toLowerCase();

  const libraryOptions = useMemo<POI[]>(() => {
    if (!query) {
      const tripIds = new Set([
        ...pinnedPoiIds,
        ...scheduledPois.map((visit) => visit.poiId),
      ]);
      return pois
        .filter((poi) => tripIds.has(poi.id))
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, MAX_LIBRARY_OPTIONS);
    }
    return pois
      .filter((poi) => poi.name.toLowerCase().includes(query))
      .sort((a, b) => {
        const aStarts = a.name.toLowerCase().startsWith(query);
        const bStarts = b.name.toLowerCase().startsWith(query);
        return (
          Number(bStarts) - Number(aStarts) || a.name.localeCompare(b.name)
        );
      })
      .slice(0, MAX_LIBRARY_OPTIONS);
  }, [pinnedPoiIds, pois, query, scheduledPois]);

  const placeOptions = useMemo<PlaceResult[]>(() => {
    if (query.length < MIN_PLACES_QUERY) return [];
    const libraryUrls = new Set(
      pois.map((poi) => poi.googleMapsUrl).filter(Boolean),
    );
    return places.results
      .filter((place) => !libraryUrls.has(place.googleMapsUrl))
      .slice(0, MAX_PLACE_OPTIONS);
  }, [places.results, pois, query]);

  const options: Option[] = [
    ...libraryOptions.map((poi) => ({ kind: "poi" as const, poi })),
    ...placeOptions.map((place) => ({ kind: "place" as const, place })),
  ];
  const clampedActive = Math.min(activeIndex, Math.max(options.length - 1, 0));
  const optionCount = options.length;

  // The list hangs below the field; inside a scrolling dialog it can open
  // past the fold, so bring it into view as it opens or grows.
  useEffect(() => {
    if (open) listRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [open, optionCount]);
  const optionId = (index: number) => `${id}-option-${index}`;

  const close = () => {
    setOpen(false);
    setDraft(null);
    places.clear();
  };

  const select = (option: Option) => {
    onChange(
      option.kind === "poi"
        ? { kind: "poi", poiId: option.poi.id }
        : { kind: "place", place: option.place },
    );
    close();
  };

  const handleInput = (text: string) => {
    setDraft(text);
    setOpen(true);
    setActiveIndex(0);
    if (value) onChange(null);
    if (text.trim().length >= MIN_PLACES_QUERY) places.search(text);
    else places.clear();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (!options.length) return;
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((clampedActive + step + options.length) % options.length);
    } else if (event.key === "Enter") {
      if (open && options[clampedActive]) {
        event.preventDefault();
        select(options[clampedActive]);
      }
    } else if (event.key === "Escape") {
      if (open || draft !== null) {
        // Handled here: keep the surrounding dialog open.
        event.preventDefault();
        event.stopPropagation();
        close();
      }
    }
  };

  const showPlacesGroup = query.length >= MIN_PLACES_QUERY;
  const hasPlacesStatus = showPlacesGroup && (places.loading || !!places.error);

  const renderOption = (option: Option, index: number) => {
    const active = index === clampedActive;
    const isPlace = option.kind === "place";
    return (
      <li
        key={isPlace ? `place-${option.place.placeId}` : option.poi.id}
        id={optionId(index)}
        role="option"
        aria-selected={active}
        onMouseDown={(event) => {
          // Keep focus in the input so blur doesn't close before the pick.
          event.preventDefault();
          select(option);
        }}
        onMouseEnter={() => setActiveIndex(index)}
        className={clsx(
          "flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5",
          active && "bg-subtle",
        )}
      >
        <span
          className="material-symbols-outlined grid size-7 shrink-0 place-items-center rounded-lg bg-subtle text-base text-secondary"
          aria-hidden
        >
          {isPlace ? "location_on" : "bookmark"}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-primary">
            {optionLabel(option)}
          </span>
          {isPlace && (
            <span className="block truncate text-xs text-tertiary">
              {option.place.address}
            </span>
          )}
        </span>
        {isPlace && (
          <span className="shrink-0 rounded-md bg-success-bg px-1.5 py-0.5 text-2xs font-bold text-success">
            New POI
          </span>
        )}
      </li>
    );
  };

  return (
    <div className="relative min-w-0">
      <label htmlFor={inputId} className="form-label">
        {label}
      </label>
      <div className="relative">
        <span
          className={clsx(
            "material-symbols-outlined pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-lg",
            value ? "text-primary" : "text-tertiary",
          )}
          aria-hidden
        >
          {icon}
        </span>
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          aria-activedescendant={
            open && options.length ? optionId(clampedActive) : undefined
          }
          autoComplete="off"
          value={draft ?? selectedName}
          onChange={(event) => handleInput(event.target.value)}
          onFocus={(event) => {
            event.target.select();
            setOpen(true);
          }}
          onBlur={close}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={clsx(
            "input-base truncate pl-9",
            (value || draft) && !disabled && "pr-9",
            value && draft === null && "font-semibold",
          )}
        />
        {(value || draft) && !disabled && (
          <Button
            variant="icon"
            size="sm"
            icon="close"
            aria-label={`Clear ${label}`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              onChange(null);
              setDraft("");
              places.clear();
              inputRef.current?.focus();
            }}
            className="absolute right-1.5 top-1/2 -translate-y-1/2"
          />
        )}
      </div>
      {value?.kind === "place" && (
        <p className="mb-0 mt-1 flex items-center gap-1 text-xs font-semibold text-success">
          <span
            className="material-symbols-outlined icon-match-text"
            aria-hidden
          >
            add_circle
          </span>
          New · added to your library when you save
        </p>
      )}
      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={`${label} suggestions`}
          className="custom-scrollbar absolute left-0 right-0 top-full z-50 m-0 mt-1 max-h-72 min-w-64 list-none overflow-y-auto rounded-xl border border-border bg-surface p-1.5 shadow-lg"
        >
          {libraryOptions.length > 0 && (
            <li
              role="presentation"
              className="px-2 pb-1 pt-1.5 text-2xs font-bold uppercase tracking-wider text-tertiary"
            >
              {query ? "Your library" : "In this trip"}
            </li>
          )}
          {libraryOptions.map((poi, index) =>
            renderOption({ kind: "poi", poi }, index),
          )}
          {(placeOptions.length > 0 || hasPlacesStatus) && (
            <li
              role="presentation"
              className="px-2 pb-1 pt-2 text-2xs font-bold uppercase tracking-wider text-tertiary"
            >
              Google Places
            </li>
          )}
          {placeOptions.map((place, index) =>
            renderOption(
              { kind: "place", place },
              libraryOptions.length + index,
            ),
          )}
          {hasPlacesStatus && placeOptions.length === 0 && (
            <li
              role="presentation"
              className="px-2 py-1.5 text-xs text-tertiary"
            >
              {places.error ?? "Searching…"}
            </li>
          )}
          {options.length === 0 && !hasPlacesStatus && (
            <li role="presentation" className="px-2 py-2 text-sm text-tertiary">
              {query
                ? `No places match “${draft?.trim()}”`
                : "Type to search your library and Google Places"}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
